import * as XLSX from 'xlsx-js-style';

type ExcelColumn<T> = {
  key: string;
  label: string;
  value?: (row: T) => unknown;
};

const normalizeExcelCell = (value: unknown): string | number | boolean => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'number' || typeof value === 'boolean') return value;
  if (value instanceof Date) return value.toISOString();
  if (Array.isArray(value)) return value.map((item) => normalizeExcelCell(item)).join(', ');
  if (typeof value === 'object') {
    try {
      return JSON.stringify(value);
    } catch {
      return String(value);
    }
  }
  return String(value);
};

const humanizeKey = (key: string) => key
  .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
  .replace(/_/g, ' ')
  .trim();

export const exportRowsToExcel = <T extends Record<string, any>>(
  rows: T[],
  fileName: string,
  options?: {
    sheetName?: string;
    columns?: Array<ExcelColumn<T>>;
  }
) => {
  const columns = options?.columns && options.columns.length > 0
    ? options.columns
    : Object.keys(rows[0] || {}).map((key) => ({ key, label: humanizeKey(key), value: (row: T) => row[key] }));

  const exportData = rows.map((row) => columns.reduce<Record<string, string | number | boolean>>((acc, column) => {
    const rawValue = column.value ? column.value(row) : row[column.key];
    acc[column.label] = normalizeExcelCell(rawValue);
    return acc;
  }, {}));

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(exportData), options?.sheetName || 'Sheet1');
  XLSX.writeFile(wb, fileName.endsWith('.xlsx') ? fileName : `${fileName}.xlsx`);
};
